/**
 * Interactive Binding Rule — buttons must have event bindings.
 * Pure function, no dependencies beyond types.
 */

import { foldBundleEventBindings } from "@cogo/publisher-core/event-binding-shape"
import type { FeatureRule, RuleViolation, BlockData } from "../types"

interface UiNode {
  id?: string
  type?: string
  text?: string
  href?: string
  children?: UiNode[]
  [key: string]: unknown
}

const INTERACTIVE_TYPES = new Set(["button", "link"])

function walkUi(root: UiNode | undefined, visitor: (node: UiNode) => void): void {
  if (!root) return
  visitor(root)
  if (Array.isArray(root.children)) {
    for (const child of root.children) walkUi(child, visitor)
  }
}

function getRoot(data: BlockData): UiNode | undefined {
  return (data.ui as Record<string, unknown>)?.root as UiNode | undefined
}

export const interactiveBindingRule: FeatureRule = {
  id: "interactive-binding",
  name: "Interactive Element Binding",
  detect: (data) => {
    const root = getRoot(data)
    if (!root) return false
    let found = false
    walkUi(root, (n) => {
      if (INTERACTIVE_TYPES.has(n.type ?? "")) found = true
    })
    return found
  },
  check: (data) => {
    const violations: RuleViolation[] = []
    const root = getRoot(data)
    if (!root) return violations

    // Task 4 / authority-repair (2026-08-08): fold EVERY runtime-accepted
    // binding shape. Reading `events.bindings` alone made this rule fire
    // `interactive-no-binding` at ERROR level on elements that ARE wired and DO
    // work — the agentic generator emits `events.elements` 100% of the time
    // (357/357 committed events.json), so on generated blocks the rule was
    // wrong about every bound control. Same fact as @cogo/validator's
    // normalizeEventBindings and the publishers'; one implementation, imported
    // via the browser-safe subpath (the publisher-core root pulls node:crypto).
    const bindings = foldBundleEventBindings(data.events)
    const boundIds = new Set<string>()
    for (const key of Object.keys(bindings)) {
      boundIds.add(key.split(":")[0])
    }

    walkUi(root, (node) => {
      if (!INTERACTIVE_TYPES.has(node.type ?? "")) return
      const id = node.id ?? ""
      if (!id) return
      // Skip links with href
      if (node.type === "link" && node.href) return
      if (boundIds.has(id)) return

      const text = node.text ?? ""
      violations.push({
        rule: "interactive-no-binding",
        level: "error",
        message: `Button '${id}'${text ? ` ("${text.slice(0, 30)}")` : ""} has no event binding`,
        file: "events",
      })
    })

    return violations
  },
}
